import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderKanban,
  BookOpen,
  PlusCircle,
  Activity,
  Film,
  CreditCard,
  History,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UploadCloud,
  HardDrive,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { user, logout } = useAuth();

  const menuItems = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/projetos', label: 'Meus Projetos', icon: FolderKanban },
    { to: '/aulas', label: 'Minhas Aulas', icon: BookOpen },
    { to: '/projetos?import=batch', label: 'Importar Aulas (ZIP)', icon: UploadCloud },
    { to: '/nova-videoaula', label: 'Nova Videoaula', icon: PlusCircle },
    { to: '/processamentos', label: 'Processamentos', icon: Activity },
    { to: '/transferencias', label: 'Transferências Drive', icon: HardDrive },
    { to: '/biblioteca', label: 'Biblioteca de Vídeos', icon: Film },
    { to: '/meu-plano', label: 'Meu Plano', icon: CreditCard },
    { to: '/historico-consumo', label: 'Histórico de Consumo', icon: History },
    { to: '/minha-conta', label: 'Minha Conta', icon: User },
  ];

  return (
    <aside
      className={`fixed top-0 left-0 z-30 h-screen bg-surface border-r border-bdr transition-all duration-300 flex flex-col justify-between ${
        collapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Brand Header */}
      <div>
        <div className="h-20 flex items-center justify-between px-4 border-b border-bdr">
          {!collapsed ? (
            <div className="flex items-center gap-2 overflow-hidden">
              <img
                src="/logo-edurender.png"
                alt="EduRender"
                className="h-9 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <img
                src="/symbol-edurender.png"
                alt="EduRender"
                className="h-9 w-9 object-contain"
              />
            </div>
          )}

          <button
            onClick={onToggle}
            className="p-1.5 rounded-md hover:bg-surface-secondary text-txt-secondary hover:text-txt-primary transition-colors"
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-160px)]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-brand-light text-brand-primary border-l-4 border-brand-primary shadow-sm font-semibold'
                      : 'text-txt-primary hover:bg-surface-secondary hover:text-txt-primary'
                  } ${collapsed ? 'justify-center px-0' : ''}`
                }
                title={collapsed ? item.label : undefined}
              >
                <Icon size={20} className="shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Footer User Info */}
      <div className="p-3 border-t border-bdr bg-surface-secondary">
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-9 h-9 rounded-full bg-brand-light border border-orange-200 text-brand-primary flex items-center justify-center font-bold text-sm shrink-0">
                {user?.fullName?.charAt(0) || 'U'}
              </div>
              <div className="truncate">
                <p className="text-xs font-semibold text-txt-primary truncate">
                  {user?.fullName || 'Usuário'}
                </p>
                <p className="text-[11px] text-txt-secondary truncate">
                  {user?.email || 'cliente@edurender.com.br'}
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-txt-muted hover:text-status-error rounded-md transition-colors"
              title="Sair da conta"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              onClick={logout}
              className="p-2 text-txt-muted hover:text-status-error rounded-md transition-colors"
              title="Sair da conta"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
